
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Globe, Clock, User as UserIcon, UserCog, Search, X, MessageCircle, User, Package, DollarSign, CheckCircle2, AlertCircle, AlertTriangle, UserPlus, Pencil, Trash2 } from "lucide-react";
import { SlotContactSelector } from '@/components/deals/SlotContactSelector';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGoBack } from "@/hooks/useGoBack";
import { createPageUrl, formatCurrency } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getAvatarColor } from "@/lib/utils";
import { useCase } from "@/hooks/useCases";
import { useCasePaymentStatus } from "@/hooks/useCasePaymentStatus";
import { useAppUsers } from "@/hooks/useAppUsers";
import { useCaseEvents } from "@/hooks/useCaseEvents";
import { useTranslation } from "react-i18next";
import { formatDateLong } from "@/lib/localizedDateUtils";
import { useUserTimezone } from "@/hooks/useUserTimezone";

import { supabase } from "@/integrations/supabase/client";

import CaseOverviewInfo from "@/components/cases/CaseOverviewInfo";
import CaseTimelineTab from "@/components/cases/CaseTimelineTab";
import CaseNotesTab from "@/components/cases/CaseNotesTab";
import CaseStagesTab from "@/components/cases/stages/CaseStagesTab";
import CaseServicesTab from "@/components/cases/services/CaseServicesTab";
import CaseDocumentsTab from "@/components/cases/CaseDocumentsTab";
import CaseTasksTab from "@/components/cases/CaseTasksTab";
import CaseDetailSidebar from "@/components/cases/CaseDetailSidebar";
import CaseProgressBar from "@/components/cases/stages/CaseProgressBar";
import { useCaseMilestones } from "@/hooks/useCaseMilestones";
import AddServiceToCaseModal from "@/components/cases/AddServiceToCaseModal";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const statusColors = {
  consultation: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  denied: "bg-rose-100 text-rose-700",
  closed: "bg-slate-100 text-slate-700",
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-700",
};

const priorityColors = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-blue-100 text-blue-600",
  high: "bg-orange-100 text-orange-600",
  urgent: "bg-rose-100 text-rose-600"
};

export default function CaseDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('id');
  const { t, i18n } = useTranslation();
  const { userTimezone } = useUserTimezone();
  const goBack = useGoBack('/cases');
  
  // Use Supabase hooks
  const { caseData, isLoading, error, refetch, updateCase } = useCase(caseId);
  const { users } = useAppUsers();
  const { nextBiometrics } = useCaseEvents(caseId);
  
  // Payment status for this case's deal
  const dealId = caseData?.item?.deal?.id;
  const { data: paymentStatus } = useCasePaymentStatus(dealId);
  const { milestones: caseMilestones = [] } = useCaseMilestones(caseId);
  
  // Lazy load contacts for dropdowns - only when needed
  const [allContacts, setAllContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  const loadContactsForDropdown = async () => {
    if (allContacts.length > 0 || loadingContacts) return;
    setLoadingContacts(true);
    try {
      const { data } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_email, primary_phone')
        .order('first_name')
        .limit(500);
      setAllContacts(data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
    setLoadingContacts(false);
  };
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("milestones");

  const [showSalesConsultantDialog, setShowSalesConsultantDialog] = useState(false);
  const [showCustomerExperienceDialog, setShowCustomerExperienceDialog] = useState(false);
  const [showCaseAnalystDialog, setShowCaseAnalystDialog] = useState(false);

  const [showPointOfContactDialog, setShowPointOfContactDialog] = useState(false);
  const [showPayerDialog, setShowPayerDialog] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);

  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editingCaseContact, setEditingCaseContact] = useState(null);
  const [selectedNewContact, setSelectedNewContact] = useState("");
  const [selectedContactObj, setSelectedContactObj] = useState(null);
  const [selectedNewRole, setSelectedNewRole] = useState("");

  // State for filling empty slots (temporary for data migration)
  const [editingSlot, setEditingSlot] = useState(null);
  
  // State for adding extra person to case (temporary for migration)
  const [showAddPersonDialog, setShowAddPersonDialog] = useState(false);
  const [newPersonRole, setNewPersonRole] = useState('');

  // Derived data from caseData
  const caseContacts = useMemo(() => caseData?.case_contacts || [], [caseData]);
  const salesItemConfig = useMemo(() => caseData?.item?.catalog || null, [caseData]);
  
  // Extract assigned users from assignments
  const salesConsultant = useMemo(() => {
    const assignment = caseData?.assignments?.find(a => a.org_role === 'sales_consultant' && a.active !== false);
    return assignment?.user || null;
  }, [caseData]);
  
  const customerExperience = useMemo(() => {
    const assignment = caseData?.assignments?.find(a => a.org_role === 'customer_experience' && a.active !== false);
    return assignment?.user || null;
  }, [caseData]);
  
  const caseAnalyst = useMemo(() => {
    const assignment = caseData?.assignments?.find(a => a.org_role === 'case_analyst' && a.active !== false);
    return assignment?.user || null;
  }, [caseData]);

  // Main applicant for page title
  const mainApplicant = useMemo(() => {
    const applicantCC = (caseData?.case_contacts || []).find(cc => cc.role === 'applicant' && cc.contact);
    if (applicantCC) return applicantCC.contact;
    return (caseData?.case_contacts || []).find(cc => cc.role === 'beneficiary' && cc.contact)?.contact || null;
  }, [caseData]);
  const applicantName = mainApplicant
    ? `${mainApplicant.first_name || ''} ${mainApplicant.last_name || ''}`.trim() || null
    : null;
  const applicantANumber = mainApplicant?.metadata?.a_number || null;

  // Get ALL points of contact (up to 2)
  const pointsOfContact = useMemo(() => {
    const pocs = caseContacts.filter(cc => 
      (cc.is_point_of_contact === true || cc.role === 'point_of_contact') && 
      cc.contact
    );
    // Limit to 2 POCs
    return pocs.slice(0, 2).map(cc => cc.contact);
  }, [caseContacts]);

  // Keep backward compatibility for single POC reference
  const pointOfContact = pointsOfContact[0] || null;

  // Payer - find by is_payer flag
  const payer = useMemo(() => {
    const payerByFlag = caseContacts.find(cc => cc.is_payer === true && cc.contact);
    if (payerByFlag) return payerByFlag.contact;
    
    const payerByRole = caseContacts.find(cc => cc.role === 'payer' && cc.contact);
    return payerByRole?.contact || null;
  }, [caseContacts]);

  // Check if payer is a main case person (not a dedicated payer role)
  const isPayerFromMainCase = useMemo(() => {
    const payerContact = caseContacts.find(cc => cc.is_payer === true || cc.role === 'payer');
    if (!payerContact) return false;
    
    // If the payer has a main role (applicant, beneficiary, dependent), they're "from main case"
    return ['applicant', 'beneficiary', 'dependent', 'petitioner'].includes(payerContact.role);
  }, [caseContacts]);

  // Extract deal from case item
  const deal = useMemo(() => caseData?.item?.deal || null, [caseData]);
  const isDealCancelled = deal?.deal_status === 'cancelled';

  // Check if a contact is the payer
  const isContactPayer = (contactId) => {
    if (!contactId) return false;
    const cc = caseContacts.find(c => c.contact_id === contactId);
    if (cc?.is_payer) return true;
    return payer?.id === contactId;
  };

  // Helper to check if a contact is POC
  const isContactPOC = (contactId) => {
    const cc = caseContacts.find(c => c.contact_id === contactId);
    if (cc?.is_point_of_contact) return true;
    if (cc?.role === 'point_of_contact') return true;
    return pointOfContact?.id === contactId;
  };

  // Auto-assign sales consultant from deal if case has none
  useEffect(() => {
    if (!caseData || salesConsultant || !deal?.sold_by?.id) return;
    // Only auto-assign if there's no existing active assignment for sales_consultant
    const alreadyAssigned = caseData.assignments?.some(a => a.org_role === 'sales_consultant' && a.active !== false);
    if (alreadyAssigned) return;
    handleAssignUser(deal.sold_by.id, 'sales_consultant');
  }, [caseData?.id, deal?.sold_by?.id]);

  // Navigate away if no case ID
  useEffect(() => {
    if (!caseId) {
      navigate(createPageUrl("Cases"));
    }
  }, [caseId, navigate]);

  const getCaseNumber = (id) => {
    if (!id) return '';
    const parts = id.split('-');
    if (parts.length >= 2) {
      return `#2025-${parts[1].substring(0, 4).toUpperCase()}`;
    }
    return `#${id.substring(0, 8)}`;
  };

  const handleAssignUser = async (userId, orgRole) => {
    if (!caseData?.id) return;
    
    setIsProcessing(true);
    try {
      // Check if there's an existing active assignment for this role
      const existingAssignment = caseData.assignments?.find(
        a => a.org_role === orgRole && a.active !== false
      );

      if (existingAssignment) {
        // Deactivate the existing assignment
        await supabase
          .from('case_assignments')
          .update({ unassigned_at: new Date().toISOString() })
          .eq('id', existingAssignment.id);
      }

      // Create new assignment
      const { error } = await supabase
        .from('case_assignments')
        .insert({
          case_id: caseData.id,
          user_id: userId,
          org_role: orgRole,
          permission: 'editor',
          assigned_at: new Date().toISOString()
        });

      if (error) throw error;

      await refetch();
      setShowSalesConsultantDialog(false);
      setShowCustomerExperienceDialog(false);
      setShowCaseAnalystDialog(false);
    } catch (error) {
      console.error("Error assigning user:", error);
      alert("Error: " + error.message);
    }
    setIsProcessing(false);
  };

  const handleChangePointOfContact = async (contactId) => {
    if (!caseData?.id) return;
    
    setIsProcessing(true);
    try {
      // 1. Remove is_point_of_contact from all contacts in this case
      const currentPOC = caseContacts.find(cc => cc.is_point_of_contact === true);
      if (currentPOC) {
        await supabase
          .from('case_contacts')
          .update({ is_point_of_contact: false })
          .eq('id', currentPOC.id);
      }
      
      // 2. Set is_point_of_contact = true on the selected contact
      const targetContact = caseContacts.find(cc => cc.contact_id === contactId);
      if (targetContact) {
        await supabase
          .from('case_contacts')
          .update({ is_point_of_contact: true })
          .eq('id', targetContact.id);
      }

      setShowPointOfContactDialog(false);
      await refetch();
    } catch (error) {
      console.error("Error changing point of contact:", error);
      alert("Error: " + error.message);
    }
    setIsProcessing(false);
  };

  // Handle changing the payer (from Quick Actions)
  const handleChangePayer = async (contactId) => {
    if (!caseData?.id) return;
    
    setIsProcessing(true);
    try {
      // 1. Remove is_payer from current payer
      const currentPayer = caseContacts.find(cc => cc.is_payer === true);
      if (currentPayer) {
        await supabase
          .from('case_contacts')
          .update({ is_payer: false })
          .eq('id', currentPayer.id);
      }
      
      // 2. Set is_payer = true on the selected contact
      const targetContact = caseContacts.find(cc => cc.contact_id === contactId);
      if (targetContact) {
        await supabase
          .from('case_contacts')
          .update({ is_payer: true })
          .eq('id', targetContact.id);
      }

      setShowPayerDialog(false);
      await refetch();
    } catch (error) {
      console.error("Error changing payer:", error);
      alert("Error: " + error.message);
    }
    setIsProcessing(false);
  };

  // Handle POC with external third-party contact
  const handleChangePointOfContactExternal = async (contactId) => {
    if (!caseData?.id) return;
    
    setIsProcessing(true);
    try {
      // 1. Remove is_point_of_contact from current POC
      const currentPOC = caseContacts.find(cc => cc.is_point_of_contact === true);
      if (currentPOC) {
        await supabase
          .from('case_contacts')
          .update({ is_point_of_contact: false })
          .eq('id', currentPOC.id);
      }
      
      // 2. Check if contact already exists in case
      const existingContact = caseContacts.find(cc => cc.contact_id === contactId);
      if (existingContact) {
        // Just mark as POC
        await supabase
          .from('case_contacts')
          .update({ is_point_of_contact: true })
          .eq('id', existingContact.id);
      } else {
        // Create new case_contact as third-party POC (won't appear in slots)
        await supabase
          .from('case_contacts')
          .insert({
            case_id: caseData.id,
            contact_id: contactId,
            role: 'point_of_contact',
            is_point_of_contact: true,
            is_assigned: true
          });
      }

      setShowPointOfContactDialog(false);
      await refetch();
    } catch (error) {
      console.error("Error changing POC:", error);
      alert("Error: " + error.message);
    }
    setIsProcessing(false);
  };

  const goToContactDetail = (contactId) => {
    if (contactId) {
      navigate(createPageUrl("ContactDetail") + "?id=" + contactId);
    }
  };

  // Handle removing a person from the case
  const handleRemovePerson = async (caseContactId, contactName) => {
    if (!caseContactId) return;
    
    if (!confirm(`Remover ${contactName} deste processo?`)) return;
    
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('case_contacts')
        .delete()
        .eq('id', caseContactId);

      if (error) throw error;

      await refetch();
    } catch (error) {
      console.error("Error removing person:", error);
      alert("Erro ao remover pessoa: " + error.message);
    }
    setIsProcessing(false);
  };

  // Handle assigning a contact to an empty slot (temporary for data migration)
  const handleAssignContactToSlot = async (slot, contactId) => {
    if (!caseData?.id || !contactId) return;
    
    setIsProcessing(true);
    try {
      // Insert new case_contact for this slot
      const { error } = await supabase
        .from('case_contacts')
        .insert({
          case_id: caseData.id,
          contact_id: contactId,
          role: slot.role,
          slot_index: slot.slotIndex || null,
          is_assigned: true,
          is_primary: slot.role === 'applicant' // Mark applicant as primary
        });

      if (error) throw error;

      await refetch();
      setEditingSlot(null);
    } catch (error) {
      console.error("Error assigning contact to slot:", error);
      alert("Erro ao atribuir contato: " + error.message);
    }
    setIsProcessing(false);
  };

  // Handle adding an extra person to the case (temporary for data migration)
  const handleAddExtraPerson = async (contactId, role) => {
    if (!caseData?.id || !contactId) return;
    
    setIsProcessing(true);
    try {
      // PAYER: Insert first, then delete old ones (to avoid trigger blocking last payer)
      if (role === 'payer') {
        // 1. First INSERT the new payer
        const { error: insertError } = await supabase
          .from('case_contacts')
          .insert({
            case_id: caseData.id,
            contact_id: contactId,
            role: 'payer',
            is_assigned: true,
            is_payer: true
          });
        
        if (insertError) throw insertError;
        
        // 2. Now DELETE old payers (except the one we just inserted)
        const { error: deleteError } = await supabase
          .from('case_contacts')
          .delete()
          .eq('case_id', caseData.id)
          .eq('role', 'payer')
          .neq('contact_id', contactId);
        
        if (deleteError) throw deleteError;
        
        // 3. Remove is_payer flag from all other contacts
        const { error: updateError } = await supabase
          .from('case_contacts')
          .update({ is_payer: false })
          .eq('case_id', caseData.id)
          .neq('contact_id', contactId);
        
        if (updateError) throw updateError;
        
        await refetch();
        setShowAddPersonDialog(false);
        setNewPersonRole('');
        setIsProcessing(false);
        return; // Already inserted, exit early
      }

      // POINT OF CONTACT: Insert first, then delete old ones
      if (role === 'point_of_contact') {
        // 1. First INSERT the new POC
        const { error: insertError } = await supabase
          .from('case_contacts')
          .insert({
            case_id: caseData.id,
            contact_id: contactId,
            role: 'point_of_contact',
            is_assigned: true,
            is_point_of_contact: true
          });
        
        if (insertError) throw insertError;
        
        // 2. Now DELETE old POCs (except the one we just inserted)
        const { error: deleteError } = await supabase
          .from('case_contacts')
          .delete()
          .eq('case_id', caseData.id)
          .eq('role', 'point_of_contact')
          .neq('contact_id', contactId);
        
        if (deleteError) throw deleteError;
        
        // 3. Remove is_point_of_contact flag from all other contacts
        const { error: updateError } = await supabase
          .from('case_contacts')
          .update({ is_point_of_contact: false })
          .eq('case_id', caseData.id)
          .neq('contact_id', contactId);
        
        if (updateError) throw updateError;
        
        await refetch();
        setShowAddPersonDialog(false);
        setNewPersonRole('');
        setIsProcessing(false);
        return; // Already inserted, exit early
      }

      // For other roles, just insert normally
      const insertData = {
        case_id: caseData.id,
        contact_id: contactId,
        role: role,
        is_assigned: true,
        is_payer: false,
        is_point_of_contact: false
      };

      const { error } = await supabase
        .from('case_contacts')
        .insert(insertData);

      if (error) throw error;

      await refetch();
      setShowAddPersonDialog(false);
      setNewPersonRole('');
    } catch (error) {
      console.error("Error adding person to case:", error);
      alert("Erro ao adicionar pessoa: " + error.message);
    }
    setIsProcessing(false);
  };

  const getRoleLabel = (role) => {
    if (!role) return t('common.unknown');
    
    // Payer gets special "$" prefix for consistency
    if (role === "payer") return `$ ${t('caseDetail.roles.payer')}`;
    if (role === "point_of_contact") return t('caseDetail.pointOfContact');
    
    if (!salesItemConfig) {
      if (role === "applicant") return t('caseDetail.roles.applicant');
      if (role === "beneficiary") return t('caseDetail.roles.beneficiary');
      if (role === "dependent") return t('caseDetail.roles.dependent');
      return role.replace(/_/g, ' ');
    }

    if (role === "applicant") return salesItemConfig.primary_person_label || t('caseDetail.roles.applicant');
    if (role === "beneficiary") return salesItemConfig.secondary_person_label || t('caseDetail.roles.beneficiary');
    if (role === "dependent") return salesItemConfig.dependent_label || t('caseDetail.roles.dependent');
    
    return role.replace(/_/g, ' ');
  };

  const getRoleBadgeColor = (roleValue) => {
    if (!roleValue) return "bg-slate-100 text-slate-700";
    
    if (roleValue === "applicant" || roleValue === "beneficiary") return "bg-blue-100 text-blue-700";
    if (roleValue === "dependent") return "bg-purple-100 text-purple-700";
    if (roleValue === "payer") return "bg-emerald-100 text-emerald-700";
    if (roleValue === "point_of_contact") return "bg-amber-100 text-amber-700";
    return "bg-slate-100 text-slate-700";
  };

  // Generate slots based on catalog configuration
  const slots = useMemo(() => {
    const catalog = caseData?.item?.catalog;
    const numDependents = caseData?.item?.num_dependents || 0;
    const enabledRoles = catalog?.enabled_roles || ['applicant'];
    const roleLabels = catalog?.role_labels || {};
    const allowsDependents = catalog?.allows_dependents ?? true;
    
    const result = [];
    
    // 1. APPLICANT FIRST (main role)
    if (enabledRoles.includes('applicant')) {
      result.push({
        role: 'applicant',
        label: catalog?.primary_person_label || roleLabels.applicant || 'Applicant',
        slotIndex: null,
        required: true,
        isDedicatedSlot: false
      });
    }
    
    // 2. SLOT FOR PAYER - only if there's a DEDICATED payer
    // (someone with role='payer' who is actually a payer, not just is_payer flag on another role)
    // If someone like an applicant has is_payer=true, they show the "$ Pagador" badge in their slot
    const hasDedicatedPayer = caseContacts.some(c => 
      c.role === 'payer' && c.is_payer !== false
    );
    const payerWithOtherRole = caseContacts.find(c => 
      c.is_payer === true && c.role !== 'payer'
    );
    
    // Only create payer slot if there's a dedicated payer, or no one is marked as payer yet
    if (hasDedicatedPayer || !payerWithOtherRole) {
      result.push({
        role: 'payer',
        label: 'Pagador',
        slotIndex: null,
        required: true,
        isDedicatedSlot: true
      });
    }
    
    // POC slots removed - exclusive POCs only appear in CONTATO card
    
    // Slot for beneficiary/secondary
    if (enabledRoles.includes('beneficiary') || enabledRoles.includes('secondary')) {
      result.push({
        role: 'beneficiary',
        label: catalog?.secondary_person_label || roleLabels.beneficiary || 'Beneficiary',
        slotIndex: null,
        required: false,
        isDedicatedSlot: false
      });
    }
    
    // Slot for petitioner
    if (enabledRoles.includes('petitioner')) {
      result.push({
        role: 'petitioner',
        label: roleLabels.petitioner || 'Petitioner',
        slotIndex: null,
        required: false,
        isDedicatedSlot: false
      });
    }

    
    // Dependent slots based on num_dependents
    if (allowsDependents) {
      for (let i = 1; i <= numDependents; i++) {
        result.push({
          role: 'dependent',
          label: `${catalog?.dependent_label || 'Dependent'} ${i}`,
          slotIndex: i,
          required: false,
          isDedicatedSlot: false
        });
      }
    }
    
    return result;
  }, [caseData]);

  // Map slots with existing case_contacts
  const slotsWithContacts = useMemo(() => {
    return slots.map((slot) => {
      // For dependents, match by slot_index
      if (slot.role === 'dependent' && slot.slotIndex) {
        const dependents = caseContacts.filter(c => c.role === 'dependent');
        const assigned = dependents.find(d => d.slot_index === slot.slotIndex) || 
                         dependents[slot.slotIndex - 1];
        return { ...slot, caseContact: assigned || null };
      }
      
      // For payer slot, ONLY find contacts with role='payer' (dedicated payer)
      // Do NOT use is_payer flag here - those contacts show the payer badge in their own role slot
      if (slot.role === 'payer') {
        const assigned = caseContacts.find(c => c.role === 'payer' && c.is_payer !== false);
        return { ...slot, caseContact: assigned || null };
      }
      
      // For other roles, find by role
      const assigned = caseContacts.find(c => c.role === slot.role);
      return { ...slot, caseContact: assigned || null };
    });
  }, [slots, caseContacts]);

  // Extra contacts not in slots (e.g., manually added)
const extraContacts = useMemo(() => {
  const slottedContactIds = new Set(
    slotsWithContacts
      .filter(s => s.caseContact?.contact_id)
      .map(s => s.caseContact.contact_id)
  );
  
  return caseContacts.filter(cc => {
    // Already in a slot? Don't show again
    if (slottedContactIds.has(cc.contact_id)) return false;
    if (!cc.contact_id) return false;
    
    // Payers now have dedicated slots, don't show in extras
    if (cc.role === 'payer') return false;
    
    // POCs now have dedicated slots, don't show in extras
    if (cc.role === 'point_of_contact') return false;
    
    return true;
  });
}, [caseContacts, slotsWithContacts]);

  // Check if dependents can be added
  const canAddDependents = useMemo(() => {
    const catalog = caseData?.item?.catalog;
    return catalog?.allows_dependents ?? true;
  }, [caseData]);

  // Available roles for adding extra people based on item config
  const availableRolesToAdd = useMemo(() => {
    const enabledRoles = salesItemConfig?.enabled_roles || ['applicant'];
    const roleLabels = salesItemConfig?.role_labels || {};
    const allowsDependents = salesItemConfig?.allows_dependents ?? true;
    
    const roles = [];
    
    // Always include Payer and POC first (cross-item roles)
    roles.push({ value: 'payer', label: 'Pagador' });
    roles.push({ value: 'point_of_contact', label: 'Ponto de Contato' });
    
    // Add enabled roles from item config
    if (enabledRoles.includes('applicant')) {
      roles.push({
        value: 'applicant',
        label: roleLabels.applicant || 'Applicant'
      });
    }
    
    if (enabledRoles.includes('beneficiary') || enabledRoles.includes('secondary')) {
      roles.push({
        value: 'beneficiary',
        label: roleLabels.beneficiary || roleLabels.secondary || 'Beneficiary'
      });
    }
    
    if (enabledRoles.includes('petitioner')) {
      roles.push({
        value: 'petitioner',
        label: roleLabels.petitioner || 'Petitioner'
      });
    }
    
    if (allowsDependents) {
      roles.push({
        value: 'dependent',
        label: 'Dependente'
      });
    }
    
    return roles;
  }, [salesItemConfig]);

  // Handler to open add person dialog with default role
  const handleOpenAddPersonDialog = () => {
    if (availableRolesToAdd.length > 0) {
      setNewPersonRole(availableRolesToAdd[0].value);
    }
    setShowAddPersonDialog(true);
  };

  const getPeopleInCase = () => {
    if (!caseContacts || caseContacts.length === 0) return [];
    
    return caseContacts.filter(cc => 
      cc.role !== 'payer' && cc.role !== 'point_of_contact'
    ).sort((a, b) => {
      if (a.is_assigned && !b.is_assigned) return -1;
      if (!a.is_assigned && b.is_assigned) return 1;
      return (a.slot_index || 0) - (b.slot_index || 0);
    });
  };

  // Check if POC is someone not in main slots (for showing in CONTACT section on right)
  const shouldShowPOCInContact = () => {
    if (!caseContacts || caseContacts.length === 0 || !pointOfContact) return false;
    
    // Check if POC has a main role (applicant, beneficiary, payer, etc)
    const pocAsMainRole = caseContacts.find(cc => 
      cc.contact_id === pointOfContact.id && 
      ['applicant', 'beneficiary', 'dependent', 'petitioner', 'payer'].includes(cc.role)
    );
    
    // Only show in contact section if POC is NOT in a main role (is ONLY a POC)
    return !pocAsMainRole;
  };

  const handleEditContact = (caseContact) => {
    setEditingCaseContact(caseContact);
    setSelectedNewContact(caseContact?.contact_id || "");
    setSelectedNewRole(caseContact?.role || "");
    setSelectedContactObj(null);
    setShowEditContactModal(true);
  };

  // Handle assigning contact to an empty slot
  const handleAssignToSlot = async (slot) => {
    // If slot already has a caseContact record, edit it
    if (slot.caseContact) {
      setEditingCaseContact(slot.caseContact);
      setSelectedNewContact(slot.caseContact.contact_id || "");
    } else {
      // Create a temporary object for the slot
      setEditingCaseContact({
        id: null, // No ID means we'll create new
        role: slot.role,
        slot_index: slot.slotIndex,
        slot_label: slot.label
      });
      setSelectedNewContact("");
    }
    setSelectedContactObj(null);
    setShowEditContactModal(true);
  };

  // Add dependent slot
  const handleAddDependentSlot = async () => {
    if (!caseData?.item?.id) return;
    
    setIsProcessing(true);
    try {
      const currentNum = caseData.item.num_dependents || 0;
      const { error } = await supabase
        .from('items')
        .update({ num_dependents: currentNum + 1 })
        .eq('id', caseData.item.id);
      
      if (error) throw error;
      await refetch();
    } catch (error) {
      console.error("Error adding dependent slot:", error);
      alert("Error: " + error.message);
    }
    setIsProcessing(false);
  };

  const handleRemoveDependentSlot = async (slotIndex) => {
    if (!caseData?.item?.id) return;

    setIsProcessing(true);
    try {
      const currentNum = caseData.item.num_dependents || 0;
      if (currentNum <= 0) return;

      // Remover case_contact associado a este slot se existir
      const contactInSlot = caseContacts.find(
        c => c.role === 'dependent' && c.slot_index === slotIndex
      );
      if (contactInSlot) {
        await supabase
          .from('case_contacts')
          .delete()
          .eq('id', contactInSlot.id);
      }

      // Decrementar num_dependents
      await supabase
        .from('items')
        .update({ num_dependents: currentNum - 1 })
        .eq('id', caseData.item.id);

      await refetch();
    } catch (error) {
      console.error("Error removing dependent slot:", error);
    }
    setIsProcessing(false);
  };

  const handleSaveContactChange = async () => {
    if (!selectedNewContact || !editingCaseContact || !caseData?.id) return;

    setIsProcessing(true);
    try {
      const role = editingCaseContact.role;
      
      // Special handling for payer slot
      if (role === 'payer') {
        // 1. Remove ALL payers (by is_payer flag AND by role='payer')
        // Find all records that have payer role (must be deleted to avoid constraint violation)
        const payersByRole = caseContacts.filter(c => c.role === 'payer');
        // Find all records that have is_payer flag but different role
        const payersByFlag = caseContacts.filter(c => c.is_payer === true && c.role !== 'payer');
        
        // Delete all role='payer' records (they violate constraint if we add new one)
        for (const payer of payersByRole) {
          await supabase
            .from('case_contacts')
            .delete()
            .eq('id', payer.id);
        }
        
        // Remove is_payer flag from other contacts
        for (const payer of payersByFlag) {
          await supabase
            .from('case_contacts')
            .update({ is_payer: false })
            .eq('id', payer.id);
        }
        
        // 2. Check if selected contact already exists in case
        const existingContact = caseContacts.find(c => c.contact_id === selectedNewContact);
        if (existingContact) {
          // Update existing to be payer
          await supabase
            .from('case_contacts')
            .update({ is_payer: true })
            .eq('id', existingContact.id);
        } else {
          // Create new case_contact as payer
          await supabase
            .from('case_contacts')
            .insert({
              case_id: caseData.id,
              contact_id: selectedNewContact,
              role: 'payer',
              is_payer: true,
              is_assigned: true
            });
        }
        
        await refetch();
        setShowEditContactModal(false);
        setEditingCaseContact(null);
        setSelectedNewContact("");
        setIsProcessing(false);
        return;
      }
      
      // Special handling for point_of_contact slot
      if (role === 'point_of_contact') {
        const slotIndex = editingCaseContact.slotIndex || editingCaseContact.slot_index || 1;
        
        // 1. Remove current POC from this specific slot_index
        const currentPoc = caseContacts.find(c => 
          (c.is_point_of_contact === true || c.role === 'point_of_contact') &&
          (c.slot_index === slotIndex || (!c.slot_index && slotIndex === 1))
        );
        
        if (currentPoc) {
          // If current POC has ONLY POC role, delete the record entirely
          if (currentPoc.role === 'point_of_contact') {
            await supabase
              .from('case_contacts')
              .delete()
              .eq('id', currentPoc.id);
          } else {
            // Otherwise, just remove the POC flag and slot_index
            await supabase
              .from('case_contacts')
              .update({ is_point_of_contact: false, slot_index: null })
              .eq('id', currentPoc.id);
          }
        }
        
        // 2. Check if selected contact already exists in case
        const existingContact = caseContacts.find(c => c.contact_id === selectedNewContact);
        if (existingContact) {
          // Update existing to be POC with slot_index
          await supabase
            .from('case_contacts')
            .update({ is_point_of_contact: true, slot_index: slotIndex })
            .eq('id', existingContact.id);
        } else {
          // Create new case_contact as POC with slot_index
          await supabase
            .from('case_contacts')
            .insert({
              case_id: caseData.id,
              contact_id: selectedNewContact,
              role: 'point_of_contact',
              is_point_of_contact: true,
              is_assigned: true,
              slot_index: slotIndex
            });
        }
        
        await refetch();
        setShowEditContactModal(false);
        setEditingCaseContact(null);
        setSelectedNewContact("");
        setIsProcessing(false);
        return;
      }
      
      // Determine final role (user may have changed it)
      const finalRole = selectedNewRole || editingCaseContact.role;

      // If role changed to/from applicant, handle applicant swap
      if (finalRole === 'applicant' && editingCaseContact.role !== 'applicant') {
        // Remove applicant role from current applicant
        const currentApplicant = caseContacts.find(c => c.role === 'applicant');
        if (currentApplicant) {
          await supabase
            .from('case_contacts')
            .update({ role: 'beneficiary', is_primary: false })
            .eq('id', currentApplicant.id);
        }
      }

      // Default handling for other slots
      if (editingCaseContact.id) {
        // Update existing case_contact
        const { error } = await supabase
          .from('case_contacts')
          .update({ 
            contact_id: selectedNewContact,
            role: finalRole,
            is_assigned: true,
            is_primary: finalRole === 'applicant'
          })
          .eq('id', editingCaseContact.id);

        if (error) throw error;
      } else {
        // Create new case_contact for the slot
        const { error } = await supabase
          .from('case_contacts')
          .insert({
            case_id: caseData.id,
            contact_id: selectedNewContact,
            role: finalRole,
            slot_index: editingCaseContact.slot_index,
            slot_label: editingCaseContact.slot_label,
            is_assigned: true,
            is_primary: finalRole === 'applicant'
          });

        if (error) throw error;
      }

      await refetch();
      setShowEditContactModal(false);
      setEditingCaseContact(null);
      setSelectedNewContact("");
    } catch (error) {
      console.error("Error updating contact:", error);
      alert("Error: " + error.message);
    }
    setIsProcessing(false);
  };

  if (error) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={goBack}
            className="mb-6 -ml-2 text-slate-600 dark:text-slate-400 flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{t('common.error')}: {error.message}</p>
            <Button onClick={refetch}>{t('common.back')}</Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={goBack}
            className="mb-6 -ml-2 text-slate-600 dark:text-slate-400 flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
          <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400 mb-4">{t('caseDetail.caseNotFound')}</p>
            <Button onClick={() => navigate(createPageUrl("Cases"))}>
              {t('caseDetail.backToCases')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const showPOCInContact = shouldShowPOCInContact();

  return (
    <div className="bg-background min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground mb-3">
          <button onClick={goBack} className="hover:text-foreground transition-colors">Processos</button>
          <span>›</span>
          <span className="text-foreground">{caseData.item?.catalog?.name || caseData.visa_type || 'Processo'}</span>
        </div>

        {/* Page header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {applicantName || caseData.item?.catalog?.name || t('caseDetail.case')}
            </h1>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="font-mono text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded">{getCaseNumber(caseData.id)}</span>
              {caseData.item?.catalog?.name && <span className="text-[12px] text-muted-foreground">{caseData.item.catalog.name}</span>}
              {applicantANumber && <><span className="text-muted-foreground/40">•</span><span className="text-[12px] text-muted-foreground">A# {applicantANumber}</span></>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {isDealCancelled && (
              <Badge className="border-0 flex items-center gap-1 bg-destructive/15 text-destructive">
                <AlertCircle className="w-3 h-3" /> Negociação cancelada
              </Badge>
            )}
            {caseData.status && (
              <Badge className={`${statusColors[caseData.status]} border-0 capitalize`}>
                {({ preparation: 'Preparação', document_collection: 'Coleta de Documentos', petition_filed: 'Petição Protocolada', in_progress: 'Em andamento', pending: 'Pendente', approved: 'Aprovado', denied: 'Negado', closed: 'Fechado', archived: 'Arquivado' })[caseData.status] || caseData.status.replace(/_/g, ' ')}
              </Badge>
            )}
            {caseData.priority && (
              <Badge className={`${priorityColors[caseData.priority]} border-0 capitalize`}>
                {({ low: 'Baixa', medium: 'Média', high: 'Alta', urgent: 'Urgente' })[caseData.priority] || caseData.priority}
              </Badge>
            )}
            {paymentStatus && paymentStatus.status !== 'no_payments' && (
              <Badge
                className={`border-0 flex items-center gap-1 ${
                  paymentStatus.status === 'up_to_date' || paymentStatus.status === 'settled'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : paymentStatus.status === 'overdue'
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                } ${
                  (paymentStatus.status === 'overdue' || paymentStatus.status === 'partial')
                    ? 'cursor-pointer hover:opacity-80 transition-opacity'
                    : ''
                }`}
                onClick={() => {
                  if ((paymentStatus.status === 'overdue' || paymentStatus.status === 'partial') && caseData?.item?.deal?.id) {
                    navigate(`/dealdetail?id=${caseData.item.deal.id}&tab=payments`);
                  }
                }}
              >
                {paymentStatus.status === 'up_to_date' && (<><CheckCircle2 className="w-3 h-3" /> Em dia</>)}
                {paymentStatus.status === 'settled' && (<><CheckCircle2 className="w-3 h-3" /> Quitado</>)}
                {paymentStatus.status === 'overdue' && (<><AlertCircle className="w-3 h-3" /> {paymentStatus.overdueCount} {paymentStatus.overdueCount === 1 ? 'parcela' : 'parcelas'} em atraso{paymentStatus.maxDaysOverdue > 0 && ` (${paymentStatus.maxDaysOverdue} dias)`}</>)}
                {paymentStatus.status === 'partial' && (<><AlertTriangle className="w-3 h-3" /> Parcialmente em atraso{paymentStatus.maxDaysOverdue > 0 && ` (${paymentStatus.maxDaysOverdue} dias)`}</>)}
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowAddServiceModal(true)} className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              {t('caseDetail.addService')}
            </Button>
          </div>
        </div>

        {isDealCancelled && (
          <div className="mb-4 rounded-xl border border-destructive/40 bg-destructive/5 px-5 py-3 flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-destructive">
                Processo congelado — negociação cancelada
                {deal?.cancelled_at ? ` em ${new Date(deal.cancelled_at).toLocaleDateString('pt-BR')}` : ''}
              </p>
              {deal?.cancellation_reason && (
                <p className="text-muted-foreground mt-0.5">Motivo: {deal.cancellation_reason}</p>
              )}
              <p className="text-muted-foreground mt-0.5">
                As etapas estão bloqueadas.{' '}
                {deal?.id && (
                  <button
                    type="button"
                    className="underline hover:no-underline text-destructive"
                    onClick={() => navigate(`/dealdetail?id=${deal.id}`)}
                  >
                    Ver negociação
                  </button>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Info bar */}
        <div className="bg-card border border-border rounded-xl px-5 py-3 mb-4 flex items-center gap-0 shadow-sm overflow-x-auto">
          {(() => {
            const pwdMilestone = caseMilestones.find(m => m.name?.startsWith('PWD'));
            const pwdCaseNumber = pwdMilestone?.data?.case_number || null;
            const numContacts = caseContacts.filter(cc => cc.contact_id).length;
            const numDep = caseData?.item?.num_dependents || 0;
            const mainPeople = Math.max(numContacts - numDep, 0);
            const peopleDisplay = numDep > 0 ? `${numContacts} (${mainPeople} + ${numDep} dep.)` : String(numContacts);
            return [
              { label: 'Empresa Sponsor', value: caseData.sponsor_company || null },
              { label: 'Nº Caso PWD', value: pwdCaseNumber },
              { label: 'Tipo de Sponsor', value: caseData.sponsor_type ? (caseData.sponsor_type === 'interno' ? 'Interno' : 'Externo') : null },
              { label: 'Tipo', value: caseData.item?.catalog?.name || caseData.visa_type || '—' },
              { label: 'Variante', value: caseData.item?.variant_label || caseData.item?.catalog_variant || null },
              { label: 'Pessoas', value: peopleDisplay },
              { label: 'Abertura', value: caseData.created_at ? new Date(caseData.created_at).toLocaleDateString('pt-BR') : '—' },
              { label: 'Filing Target', value: caseData.filing_target || null },
            ].filter(item => item.value !== null);
          })().map((item, idx, arr) => (
            <React.Fragment key={item.label}>
              <div className="flex flex-col gap-0.5 px-4 first:pl-0">
                <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">{item.label}</span>
                <span className="text-[12px] font-medium text-foreground whitespace-nowrap">{item.value}</span>
              </div>
              {idx < arr.length - 1 && <div className="w-px h-8 bg-border flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        {/* Milestone stats above tabs */}
        {caseMilestones.length > 0 && (() => {
          const completedN = caseMilestones.filter(m => m.status_category === 'completed').length;
          const inProgressN = caseMilestones.filter(m => m.status_category === 'internal_action').length;
          const waitingN = caseMilestones.filter(m => m.status_category === 'external_wait').length;
          const actionN = caseMilestones.filter(m => m.status_category === 'action_required').length;
          return (
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-card border border-border rounded-lg px-4 py-3 text-center shadow-sm">
                <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">{completedN}</div>
                <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground mt-0.5">Concluídas</div>
              </div>
              <div className="bg-card border border-border rounded-lg px-4 py-3 text-center shadow-sm">
                <div className="text-xl font-bold font-mono text-amber-500 dark:text-amber-400">{inProgressN}</div>
                <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground mt-0.5">Em Andamento</div>
              </div>
              <div className="bg-card border border-border rounded-lg px-4 py-3 text-center shadow-sm">
                <div className="text-xl font-bold font-mono text-blue-500 dark:text-blue-400">{waitingN}</div>
                <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground mt-0.5">Aguardando</div>
              </div>
              <div className="bg-card border border-border rounded-lg px-4 py-3 text-center shadow-sm">
                <div className="text-xl font-bold font-mono text-rose-500 dark:text-rose-400">{actionN}</div>
                <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground mt-0.5">Ação Necessária</div>
              </div>
            </div>
          );
        })()}

        {/* Tab navigation — full width, outside the grid */}
        <div className="flex gap-0 border-b border-border mb-5">
          {[
            { key: 'milestones', label: 'Etapas do Processo', count: caseMilestones.length },
            { key: 'people', label: 'Pessoas', count: caseContacts.filter(cc => cc.contact_id).length },
            { key: 'timeline', label: 'Linha do Tempo', count: null },
            { key: 'documents', label: 'Documentos', count: null },
            { key: 'notes', label: 'Anotações', count: null },
            { key: 'tasks', label: 'Tarefas', count: null },
            { key: 'services', label: 'Serviços', count: null },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 justify-center px-2 py-3 text-[13px] font-medium transition-all -mb-px border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === tab.key
                  ? "text-primary border-primary font-semibold"
                  : "text-muted-foreground border-transparent hover:text-foreground"
              }`}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className={`text-[10px] font-mono rounded-full px-1.5 py-0.5 ${
                  activeTab === tab.key ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Grid: Main content + Persistent Sidebar */}
        <div className="grid lg:grid-cols-[1fr_280px] gap-6">
          <div>
            {/* Tab content */}
            {false && ( /* overview removed */
              <div>

                {/* PEOPLE IN THIS CASE — Card wrapper with compact rows */}
                <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
                  <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Pessoas Neste Processo
                  </h3>

                  <div className="divide-y divide-border">
                    {/* Render slots — compact rows */}
                    {slotsWithContacts.map((slot, index) => {
                      const cc = slot.caseContact;
                      const isAssigned = cc?.contact;
                      
                      const isPayer = isAssigned && isContactPayer(cc.contact_id);
                      const isPOC = isAssigned && isContactPOC(cc.contact_id);
                      
                      if (isAssigned) {
                        return (
                          <div key={`slot-${slot.role}-${slot.slotIndex || index}`}
                            className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-[38px] h-[38px] shrink-0 bg-gradient-to-br ${getAvatarColor(cc.contact.first_name)} rounded-full flex items-center justify-center`}>
                                <span className="text-white font-bold text-sm">
                                  {cc.contact.first_name?.charAt(0).toUpperCase() || 'U'}{cc.contact.last_name?.charAt(0).toUpperCase() || ''}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <button onClick={() => goToContactDetail(cc.contact.id)}
                                  className="font-semibold text-[13px] text-primary hover:text-primary/80 hover:underline text-left truncate block">
                                  {cc.contact.first_name} {cc.contact.last_name}
                                </button>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  <Badge className={`${getRoleBadgeColor(cc.role)} border-0 text-[10px] capitalize h-5`}>{slot.label}</Badge>
                                  {cc.is_primary && <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-0 text-[10px] h-5">{t('caseDetail.primary')}</Badge>}
                                  {isPayer && cc.role !== 'payer' && slot.role !== 'payer' && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-0 text-[10px] h-5">$ {t('caseDetail.payer')}</Badge>}
                                  {isPOC && cc.role !== 'point_of_contact' && <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 border-0 text-[10px] h-5"><MessageCircle className="w-3 h-3 mr-0.5" />POC</Badge>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                              {cc.contact?.primary_phone && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-emerald-600" title="Ligar"
                                  onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('initiate-call', { detail: { contactId: cc.contact.id, phoneNumber: cc.contact.primary_phone, contactName: `${cc.contact.first_name || ''} ${cc.contact.last_name || ''}`.trim() } })); }}>
                                  <Phone className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => handleAssignToSlot(slot)}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={() => handleRemovePerson(cc.id, `${cc.contact?.first_name || ''} ${cc.contact?.last_name || ''}`)} disabled={isProcessing}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div key={`slot-${slot.role}-${slot.slotIndex || index}`}
                            className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                            <div className="flex items-center gap-3">
                              <div className="w-[38px] h-[38px] shrink-0 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-800/40">
                                <User className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-[13px] text-amber-900 dark:text-amber-200">
                                  {slot.label}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-800/40 dark:text-amber-300 border-0 text-xs">
                                    Não preenchido durante onboarding
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setEditingSlot(slot)}
                              disabled={isProcessing}
                              className="border-amber-400 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-900/40"
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              Preencher
                            </Button>
                          </div>
                        );
                      }
                    })}

                    {/* Extra contacts — compact rows */}
                    {extraContacts.map((cc, index) => {
                      const isPayer = isContactPayer(cc.contact_id);
                      const isPOC = isContactPOC(cc.contact_id);
                      if (!cc.contact) return null;
                      return (
                        <div key={`extra-${cc.id || index}`} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-[38px] h-[38px] shrink-0 bg-gradient-to-br ${getAvatarColor(cc.contact.first_name)} rounded-full flex items-center justify-center`}>
                              <span className="text-white font-bold text-sm">
                                {cc.contact.first_name?.charAt(0).toUpperCase() || 'U'}{cc.contact.last_name?.charAt(0).toUpperCase() || ''}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <button onClick={() => goToContactDetail(cc.contact.id)}
                                className="font-semibold text-[13px] text-primary hover:text-primary/80 hover:underline text-left truncate block">
                                {cc.contact.first_name} {cc.contact.last_name}
                              </button>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <Badge className={`${getRoleBadgeColor(cc.role)} border-0 text-[10px] capitalize h-5`}>{getRoleLabel(cc.role)}</Badge>
                                {isPayer && cc.role !== 'payer' && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-0 text-[10px] h-5">$ {t('caseDetail.payer')}</Badge>}
                                {isPOC && <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 border-0 text-[10px] h-5"><MessageCircle className="w-3 h-3 mr-0.5" />POC</Badge>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {cc.contact.primary_phone && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-emerald-600" title="Ligar"
                                onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('initiate-call', { detail: { contactId: cc.contact.id, phoneNumber: cc.contact.primary_phone, contactName: `${cc.contact.first_name || ''} ${cc.contact.last_name || ''}`.trim() } })); }}>
                                <Phone className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => handleEditContact(cc)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => handleRemovePerson(cc.id, `${cc.contact?.first_name || ''} ${cc.contact?.last_name || ''}`)} disabled={isProcessing}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add Person Button inside card */}
                  <Button variant="outline" onClick={handleOpenAddPersonDialog} disabled={isProcessing}
                    className="w-full mt-3 border-dashed border-2 border-border text-muted-foreground hover:bg-accent text-xs">
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                    Adicionar Pessoa ao Processo
                  </Button>

                  {slotsWithContacts.length === 0 && extraContacts.length === 0 && (
                    <div className="p-6 text-center text-muted-foreground">
                      <UserIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-xs">{t('caseDetail.noPeopleYet')}</p>
                    </div>
                  )}
                </div>

                {/* INFORMAÇÕES DO PROCESSO — Card with info-grid */}
                <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
                  <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Informações do Processo
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Consultor de Vendas</p>
                      <div className="flex items-center gap-1.5">
                        <p className="text-[13px] font-semibold text-foreground">
                          {salesConsultant ? (
                            salesConsultant.first_name && salesConsultant.last_name
                              ? `${salesConsultant.first_name} ${salesConsultant.last_name?.charAt(0)}.`
                              : salesConsultant.full_name
                          ) : (
                            <span className="text-muted-foreground font-normal">{t('caseDetail.notAssigned')}</span>
                          )}
                        </p>
                        <button onClick={() => setShowSalesConsultantDialog(true)} className="p-0.5 hover:bg-accent rounded transition-colors">
                          <UserCog className="w-3.5 h-3.5 text-primary" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Experiência do Cliente</p>
                      <div className="flex items-center gap-1.5">
                        <p className="text-[13px] font-semibold text-foreground">
                          {customerExperience ? (
                            customerExperience.first_name && customerExperience.last_name
                              ? `${customerExperience.first_name} ${customerExperience.last_name?.charAt(0)}.`
                              : customerExperience.full_name
                          ) : (
                            <span className="text-muted-foreground font-normal">{t('caseDetail.notAssigned')}</span>
                          )}
                        </p>
                        <button onClick={() => setShowCustomerExperienceDialog(true)} className="p-0.5 hover:bg-accent rounded transition-colors">
                          <UserCog className="w-3.5 h-3.5 text-primary" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Analista do Caso</p>
                      <div className="flex items-center gap-1.5">
                        <p className="text-[13px] font-semibold text-foreground">
                          {caseAnalyst ? (
                            caseAnalyst.first_name && caseAnalyst.last_name
                              ? `${caseAnalyst.first_name} ${caseAnalyst.last_name?.charAt(0)}.`
                              : caseAnalyst.full_name
                          ) : (
                            <span className="text-muted-foreground font-normal">{t('caseDetail.notAssigned')}</span>
                          )}
                        </p>
                        <button onClick={() => setShowCaseAnalystDialog(true)} className="p-0.5 hover:bg-accent rounded transition-colors">
                          <UserCog className="w-3.5 h-3.5 text-primary" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Criado em</p>
                      <p className="text-[13px] font-semibold text-foreground">
                        {caseData.created_at ? formatDateLong(caseData.created_at, { language: i18n.language, timezone: userTimezone }) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Case Overview Info */}
                <CaseOverviewInfo
                  googleDriveUrl={caseData.google_drive_url}
                  legacyStage={caseData.legacy_stage}
                  legacyPipeline={caseData.legacy_pipeline}
                  clock150DaysStart={caseData.clock_150_days_start}
                  clock150DaysPausedAt={caseData.clock_150_days_paused_at}
                  clock150DaysPausedReason={caseData.clock_150_days_paused_reason}
                  nextBiometrics={nextBiometrics}
                  premiumProcessing={caseData.premium_processing}
                />
              </div>
            )}

            {activeTab === "milestones" && (
              <CaseStagesTab caseId={caseData.id} isLocked={isDealCancelled} />
            )}

            {activeTab === "people" && (
              <div className="space-y-4">
                <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                  Pessoas neste Processo
                </h2>

                {/* Detailed person cards from slots */}
                {slotsWithContacts.map((slot, index) => {
                  const cc = slot.caseContact;
                  const isAssigned = cc?.contact;
                  const isPayer = isAssigned && isContactPayer(cc.contact_id);
                  const isPOC = isAssigned && isContactPOC(cc.contact_id);

                  if (!isAssigned) {
                    return (
                      <div
                        key={`ppl-slot-${slot.role}-${slot.slotIndex || index}`}
                        className="flex items-center justify-between p-4 border-2 border-dashed rounded-xl bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-800/40">
                            <User className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-amber-900 dark:text-amber-200">{slot.label}</h3>
                            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-800/40 dark:text-amber-300 border-0 text-xs mt-1">
                              Não preenchido
                            </Badge>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setEditingSlot(slot)} disabled={isProcessing}
                          className="border-amber-400 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-300">
                          <UserPlus className="w-4 h-4 mr-2" />Preencher
                        </Button>
                      </div>
                    );
                  }

                  const contact = cc.contact;
                  const dob = contact.date_of_birth;
                  const meta = contact.metadata || {};

                  return (
                    <div key={`ppl-slot-${slot.role}-${slot.slotIndex || index}`}
                      className={`rounded-xl p-5 ${isPOC ? "bg-sky-50 dark:bg-sky-900/20 border-2 border-sky-300 dark:border-sky-600" : "bg-card border border-border"}`}>
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 bg-gradient-to-br ${getAvatarColor(contact.first_name)} rounded-full flex items-center justify-center shadow-sm`}>
                            <span className="text-white font-bold">
                              {contact.first_name?.charAt(0).toUpperCase() || 'U'}{contact.last_name?.charAt(0).toUpperCase() || ''}
                            </span>
                          </div>
                          <div>
                            <button onClick={() => goToContactDetail(contact.id)}
                              className="font-semibold text-primary hover:text-primary/80 hover:underline text-left">
                              {contact.first_name} {contact.last_name}
                            </button>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge className={`${getRoleBadgeColor(cc.role)} border-0 text-xs capitalize`}>{slot.label}</Badge>
                              {cc.is_primary && <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-0 text-xs">{t('caseDetail.primary')}</Badge>}
                              {isPayer && cc.role !== 'payer' && slot.role !== 'payer' && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-0 text-xs">$ {t('caseDetail.payer')}</Badge>}
                              {isPOC && cc.role !== 'point_of_contact' && <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 border-0 text-xs"><MessageCircle className="w-3 h-3 mr-1" />{t('caseDetail.pointOfContact')}</Badge>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {contact.primary_phone && (
                            <Button variant="ghost" size="icon" title="Ligar"
                              onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('initiate-call', { detail: { contactId: contact.id, phoneNumber: contact.primary_phone, contactName: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() } })); }}
                              className="text-muted-foreground hover:text-emerald-600"><Phone className="w-4 h-4" /></Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => handleAssignToSlot(slot)} className="text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleRemovePerson(cc.id, `${contact.first_name || ''} ${contact.last_name || ''}`)} disabled={isProcessing}
                            className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      {/* Detail fields grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">A-Number</p>
                          <p className="font-medium text-foreground">{meta.a_number || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Data de Nascimento</p>
                          {dob ? (() => { const d = new Date(dob); return (<><p className="font-mono text-[12px] font-medium text-foreground">{d.toLocaleDateString('pt-BR')}</p><p className="font-mono text-[9px] text-muted-foreground">{d.toLocaleDateString('en-US')}</p></>); })() : <p className="font-medium text-foreground">—</p>}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Nacionalidade</p>
                          <p className="font-medium text-foreground">{contact.country_of_birth || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Telefone</p>
                          <p className="font-medium text-foreground">{contact.primary_phone || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                          <p className="font-medium text-foreground truncate">{contact.primary_email || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Endereço</p>
                          <p className="font-medium text-foreground">{contact.primary_address || '—'}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Extra contacts - detailed cards */}
                {extraContacts.map((cc, index) => {
                  const isPayer = isContactPayer(cc.contact_id);
                  const isPOC = isContactPOC(cc.contact_id);
                  const contact = cc.contact;
                  if (!contact) return null;
                  const dob = contact.date_of_birth;
                  const meta = contact.metadata || {};

                  return (
                    <div key={`ppl-extra-${cc.id || index}`}
                      className={`rounded-xl p-5 ${isPOC ? "bg-sky-50 dark:bg-sky-900/20 border-2 border-sky-300 dark:border-sky-600" : "bg-card border border-border"}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 bg-gradient-to-br ${getAvatarColor(contact.first_name)} rounded-full flex items-center justify-center shadow-sm`}>
                            <span className="text-white font-bold">
                              {contact.first_name?.charAt(0).toUpperCase() || 'U'}{contact.last_name?.charAt(0).toUpperCase() || ''}
                            </span>
                          </div>
                          <div>
                            <button onClick={() => goToContactDetail(contact.id)}
                              className="font-semibold text-primary hover:text-primary/80 hover:underline text-left">
                              {contact.first_name} {contact.last_name}
                            </button>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge className={`${getRoleBadgeColor(cc.role)} border-0 text-xs capitalize`}>{getRoleLabel(cc.role)}</Badge>
                              {isPayer && cc.role !== 'payer' && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-0 text-xs">$ {t('caseDetail.payer')}</Badge>}
                              {isPOC && <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 border-0 text-xs"><MessageCircle className="w-3 h-3 mr-1" />{t('caseDetail.pointOfContact')}</Badge>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {contact.primary_phone && (
                            <Button variant="ghost" size="icon" title="Ligar"
                              onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('initiate-call', { detail: { contactId: contact.id, phoneNumber: contact.primary_phone, contactName: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() } })); }}
                              className="text-muted-foreground hover:text-emerald-600"><Phone className="w-4 h-4" /></Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => handleEditContact(cc)} className="text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleRemovePerson(cc.id, `${contact.first_name || ''} ${contact.last_name || ''}`)} disabled={isProcessing}
                            className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">A-Number</p>
                          <p className="font-medium text-foreground">{meta.a_number || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Data de Nascimento</p>
                          {dob ? (() => { const d = new Date(dob); return (<><p className="font-mono text-[12px] font-medium text-foreground">{d.toLocaleDateString('pt-BR')}</p><p className="font-mono text-[9px] text-muted-foreground">{d.toLocaleDateString('en-US')}</p></>); })() : <p className="font-medium text-foreground">—</p>}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Nacionalidade</p>
                          <p className="font-medium text-foreground">{contact.country_of_birth || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Telefone</p>
                          <p className="font-medium text-foreground">{contact.primary_phone || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                          <p className="font-medium text-foreground truncate">{contact.primary_email || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Endereço</p>
                          <p className="font-medium text-foreground">{contact.primary_address || '—'}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Add Person Button */}
                <Button variant="outline" onClick={handleOpenAddPersonDialog} disabled={isProcessing}
                  className="w-full border-dashed border-2 border-slate-300 dark:border-slate-600 text-muted-foreground hover:bg-accent">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Adicionar Pessoa ao Processo
                </Button>

                {slotsWithContacts.length === 0 && extraContacts.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground bg-muted rounded-lg">
                    <UserIcon className="w-10 h-10 mx-auto mb-3" />
                    <p>{t('caseDetail.noPeopleYet')}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "timeline" && (
              <CaseTimelineTab caseId={caseData.id} caseCreatedAt={caseData.created_at} />
            )}

            {activeTab === "documents" && (
              <CaseDocumentsTab 
                caseId={caseData.id} 
                googleDriveUrl={caseData.google_drive_url}
                caseContacts={caseContacts}
                salesItemConfig={salesItemConfig}
              />
            )}

            {activeTab === "notes" && (
              <CaseNotesTab 
                caseId={caseData.id} 
                contactIds={caseContacts?.map(cc => cc.contact_id).filter(Boolean) || []}
              />
            )}

            {activeTab === "tasks" && (
              <CaseTasksTab caseId={caseData.id} />
            )}

            {activeTab === "services" && (
              <CaseServicesTab caseId={caseData.id} />
            )}
          </div>

          {/* Persistent Right Sidebar */}
          <div className="hidden lg:block">
            <CaseDetailSidebar
              caseId={caseId}
              caseData={caseData}
              deal={deal}
              paymentStatus={paymentStatus}
              salesConsultant={salesConsultant}
              customerExperience={customerExperience}
              caseAnalyst={caseAnalyst}
              milestones={caseMilestones}
              navigate={navigate}
              onAssignConsultant={() => setShowSalesConsultantDialog(true)}
              onAssignCX={() => setShowCustomerExperienceDialog(true)}
              onAssignAnalyst={() => setShowCaseAnalystDialog(true)}
              onUpdateCase={updateCase}
            />
          </div>
        </div>

        {/* Removed old Assign Contact Dialog */}
        {/* <Dialog open={showAssignContactDialog} onOpenChange={setShowAssignContactDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Contact</DialogTitle>
              <DialogDescription>
                Search for a contact to assign to this {selectedSlot?.slot_label?.toLowerCase()} slot
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {allContacts.length > 0 ? (
                <>
                  <div>
                    <Label>Search Contact</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                        placeholder="Search for a contact..."
                        value={searchQuery}
                        onChange={(e) => handleSearchContacts(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="border rounded-lg max-h-64 overflow-y-auto">
                      {searchResults.map((contact) => (
                        <button
                          key={contact.id}
                          type="button"
                          onClick={() => handleAssignContactToSlot(contact)}
                          disabled={isProcessing}
                          className="w-full p-3 text-left hover:bg-slate-50 border-b last:border-0"
                        >
                          <p className="font-medium">
                            {contact.first_name} {contact.last_name}
                          </p>
                          <p className="text-sm text-slate-600">{contact.primary_email}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                  <p className="text-sm text-amber-800">Cannot load contacts list</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog> */}

        {/* Payer Dialog */}
        <Dialog open={showPayerDialog} onOpenChange={setShowPayerDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Alterar Pagador</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Selecione uma das pessoas envolvidas no caso para ser o pagador:
              </p>
              
              {caseContacts.filter(cc => 
                ['applicant', 'beneficiary', 'dependent', 'petitioner'].includes(cc.role) && cc.is_assigned
              ).length > 0 ? (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {caseContacts
                    .filter(cc => ['applicant', 'beneficiary', 'dependent', 'petitioner'].includes(cc.role) && cc.is_assigned)
                    .map((cc) => (
                      <button
                        key={cc.contact_id}
                        type="button"
                        onClick={() => handleChangePayer(cc.contact_id)}
                        disabled={isProcessing}
                        className={`w-full p-3 text-left hover:bg-emerald-50 border-b last:border-0 ${
                          payer?.id === cc.contact_id ? 'bg-emerald-50 border-emerald-300' : ''
                        }`}
                      >
                        <p className="font-medium">
                          {cc.contact?.first_name} {cc.contact?.last_name}
                        </p>
                        <p className="text-sm text-slate-600 capitalize">{getRoleLabel(cc.role)}</p>
                      </button>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  {t('caseDetail.dialogs.noContactsAvailable')}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Point of Contact Dialog */}
        <Dialog open={showPointOfContactDialog} onOpenChange={setShowPointOfContactDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('caseDetail.dialogs.changePointOfContact')}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Escolha uma pessoa do caso ou busque um terceiro:
              </p>
              
              {/* Pessoas do caso */}
              {caseContacts.filter(cc => 
                ['applicant', 'beneficiary', 'dependent', 'petitioner'].includes(cc.role) && cc.is_assigned
              ).length > 0 && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {caseContacts
                    .filter(cc => ['applicant', 'beneficiary', 'dependent', 'petitioner'].includes(cc.role) && cc.is_assigned)
                    .map((cc) => (
                      <button
                        key={cc.contact_id}
                        type="button"
                        onClick={() => handleChangePointOfContact(cc.contact_id)}
                        disabled={isProcessing}
                        className={`w-full p-3 text-left hover:bg-slate-50 border-b last:border-0 ${
                          pointOfContact?.id === cc.contact_id ? 'bg-sky-50 border-sky-300' : ''
                        }`}
                      >
                        <p className="font-medium">
                          {cc.contact?.first_name} {cc.contact?.last_name}
                        </p>
                        <p className="text-sm text-slate-600 capitalize">{getRoleLabel(cc.role)}</p>
                      </button>
                    ))}
                </div>
              )}
              
              {/* Separador */}
              <div className="flex items-center gap-2">
                <div className="flex-1 border-t"></div>
                <span className="text-xs text-slate-500">ou busque um terceiro</span>
                <div className="flex-1 border-t"></div>
              </div>
              
              {/* Busca de terceiro */}
              <SlotContactSelector
                value={null}
                onSelectContact={(contact) => handleChangePointOfContactExternal(contact.id)}
                onCreateNew={() => {}}
                placeholder="Buscar contato externo..."
                excludeContactIds={caseContacts.map(cc => cc.contact_id).filter(Boolean)}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Sales Consultant Dialog */}
        <Dialog open={showSalesConsultantDialog} onOpenChange={setShowSalesConsultantDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('caseDetail.dialogs.assignSalesConsultant')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {t('caseDetail.dialogs.selectUserForRole', { role: t('caseDetail.salesConsultant') })}
              </p>
              {users.length > 0 ? (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {users.filter(u => u.active).map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => handleAssignUser(u.id, 'sales_consultant')}
                      disabled={isProcessing}
                      className={`w-full p-3 text-left hover:bg-slate-50 border-b last:border-0 ${
                        salesConsultant?.id === u.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <p className="font-medium">
                        {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                      </p>
                      <p className="text-sm text-slate-600">{u.email}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">{t('caseDetail.dialogs.noUsersAvailable')}</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Customer Experience Dialog */}
        <Dialog open={showCustomerExperienceDialog} onOpenChange={setShowCustomerExperienceDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('caseDetail.dialogs.assignCustomerExperience')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {t('caseDetail.dialogs.selectUserForRole', { role: t('caseDetail.customerExperience') })}
              </p>
              {users.length > 0 ? (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {users.filter(u => u.active).map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => handleAssignUser(u.id, 'customer_experience')}
                      disabled={isProcessing}
                      className={`w-full p-3 text-left hover:bg-slate-50 border-b last:border-0 ${
                        customerExperience?.id === u.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <p className="font-medium">
                        {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                      </p>
                      <p className="text-sm text-slate-600">{u.email}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">{t('caseDetail.dialogs.noUsersAvailable')}</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Case Analyst Dialog */}
        <Dialog open={showCaseAnalystDialog} onOpenChange={setShowCaseAnalystDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('caseDetail.dialogs.assignCaseAnalyst')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {t('caseDetail.dialogs.selectUserForRole', { role: t('caseDetail.caseAnalyst') })}
              </p>
              {users.length > 0 ? (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {users.filter(u => u.active).map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => handleAssignUser(u.id, 'case_analyst')}
                      disabled={isProcessing}
                      className={`w-full p-3 text-left hover:bg-slate-50 border-b last:border-0 ${
                        caseAnalyst?.id === u.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <p className="font-medium">
                        {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                      </p>
                      <p className="text-sm text-slate-600">{u.email}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">{t('caseDetail.dialogs.noUsersAvailable')}</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Contact Modal */}
        <Dialog open={showEditContactModal} onOpenChange={setShowEditContactModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCaseContact?.is_assigned ? t('caseDetail.changeContact') : t('caseDetail.assignContact')}
              </DialogTitle>
              <DialogDescription>
                {editingCaseContact?.is_assigned ? 
                  t('caseDetail.dialogs.replaceContactDesc') :
                  t('caseDetail.dialogs.selectContactDesc', { role: editingCaseContact?.slot_label?.toLowerCase() || getRoleLabel(editingCaseContact?.role) })
                }
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Função</Label>
                <Select value={selectedNewRole} onValueChange={setSelectedNewRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a função" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRolesToAdd.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Contato</Label>
              </div>
              <SlotContactSelector
                value={selectedContactObj}
                onSelectContact={(contact) => {
                  setSelectedContactObj(contact);
                  setSelectedNewContact(contact.id);
                }}
                onCreateNew={() => {
                  console.log('Create new contact');
                }}
                placeholder={t('caseDetail.dialogs.selectContact')}
                excludeContactIds={[]}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowEditContactModal(false)}
                disabled={isProcessing}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSaveContactChange}
                disabled={!selectedNewContact || isProcessing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Fill Empty Slot Dialog (Temporary for migration) */}
        <Dialog open={!!editingSlot} onOpenChange={(open) => !open && setEditingSlot(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Preencher Slot - {editingSlot?.label}</DialogTitle>
              <DialogDescription>
                Selecione um contato existente para atribuir a este slot.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <SlotContactSelector
                value={null}
                onSelectContact={(contact) => {
                  if (contact && editingSlot) {
                    handleAssignContactToSlot(editingSlot, contact.id);
                  }
                }}
                onCreateNew={() => {
                  // For now, just close and let them create via Contacts page
                  setEditingSlot(null);
                  navigate('/contacts?action=create');
                }}
                placeholder="Buscar contato..."
                excludeContactIds={caseContacts.map(cc => cc.contact_id).filter(Boolean)}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditingSlot(null)}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Extra Person Dialog (Temporary for migration) */}
        <Dialog open={showAddPersonDialog} onOpenChange={setShowAddPersonDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Adicionar Pessoa ao Processo</DialogTitle>
              <DialogDescription>
                Selecione o tipo de pessoa e o contato para adicionar ao processo.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Tipo de Pessoa</Label>
                <Select value={newPersonRole} onValueChange={setNewPersonRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRolesToAdd.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Contato</Label>
                <SlotContactSelector
                  value={null}
                  onSelectContact={(contact) => {
                    if (contact) {
                      handleAddExtraPerson(contact.id, newPersonRole);
                    }
                  }}
                  onCreateNew={() => {
                    setShowAddPersonDialog(false);
                    navigate('/contacts?action=create');
                  }}
                  placeholder="Buscar contato..."
                  excludeContactIds={caseContacts.map(cc => cc.contact_id).filter(Boolean)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowAddPersonDialog(false)}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Service Modal */}
        <AddServiceToCaseModal
          open={showAddServiceModal}
          onOpenChange={setShowAddServiceModal}
          caseId={caseId}
          onSuccess={refetch}
        />
      </div>
    </div>
  );
}
